import { ReactNode, useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useUnreadCounts } from "../lib/useUnreadCounts";

interface Props {
  children: ReactNode;
  variant?: "client" | "admin";
}

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

const clientNav: NavItem[] = [
  { to: "/", label: "Overview", end: true },
  { to: "/billing", label: "Billing" },
  { to: "/services", label: "Services" },
  { to: "/services/purchase", label: "Purchase" },
  { to: "/domains", label: "Domains & Email" },
  { to: "/support", label: "Support" },
  { to: "/projects", label: "Projects" },
  { to: "/profile", label: "Profile" },
];

const adminNav: NavItem[] = [
  { to: "/admin", label: "Overview", end: true },
  { to: "/admin/clients", label: "Clients" },
  { to: "/admin/services", label: "Services" },
  { to: "/admin/support", label: "Support" },
  { to: "/admin/projects", label: "Projects" },
  { to: "/admin/domain-requests", label: "Domain requests" },
  { to: "/admin/emails", label: "Emails" },
];

export function Layout({ children, variant = "client" }: Props) {
  const { user, role, displayName, signOut, suspended } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Suspended clients only get Billing + Profile in the sidebar so they can
  // settle their balance and (if needed) update their contact info. Once
  // payment lands the webhook flips portalAccess back to true and the full
  // nav reappears on next page load.
  const items =
    variant === "admin"
      ? adminNav
      : suspended
      ? clientNav.filter(
          (it) => it.to === "/billing" || it.to === "/profile"
        )
      : clientNav;
  const {
    support: unreadSupport,
    billing: unreadBilling,
    domainRequests: pendingDomainRequests,
  } = useUnreadCounts();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Map nav routes to a badge count.
  function badgeFor(to: string): number | null {
    if (to === "/support" || to === "/admin/support") {
      return unreadSupport > 0 ? unreadSupport : null;
    }
    if (to === "/billing") {
      return unreadBilling > 0 ? unreadBilling : null;
    }
    if (to === "/admin/domain-requests") {
      return pendingDomainRequests > 0 ? pendingDomainRequests : null;
    }
    return null;
  }

  // Combined indicator on the mobile hamburger button — any unread of any kind.
  // For admins this includes the domain-request queue; for clients it's 0.
  const mobileTotalUnread =
    unreadSupport + unreadBilling + pendingDomainRequests;

  // Close the drawer whenever the route changes (clicking a nav link
  // navigates and we want the menu to dismiss itself afterward).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer is open so background doesn't move under it.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [drawerOpen]);

  async function handleSignOut() {
    await signOut();
    navigate("/login");
  }

  // The same nav rendering used in both the desktop sidebar and the mobile
  // drawer — keeps active state, badges, and styling in sync.
  function renderNav(extraClass = "") {
    return (
      <nav className={`flex flex-col gap-0.5 ${extraClass}`}>
        {items.map((it) => {
          const badge = badgeFor(it.to);
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-accent-600 text-white"
                    : "text-ink-700 hover:bg-ink-100"
                }`
              }
            >
              <span>{it.label}</span>
              {badge != null && (
                <span className="rounded-full bg-accent-600 px-2 py-0.5 text-[10px] font-semibold text-white">
                  {badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    );
  }

  function renderSignedInCard() {
    return (
      <div className="rounded-lg border border-ink-100 bg-white p-3">
        <div className="text-xs text-ink-500">Signed in as</div>
        <div className="truncate text-sm font-medium">
          {displayName ?? user?.email ?? user?.phoneNumber ?? "—"}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-wider text-ink-400">
          {role}
        </div>
        <button
          onClick={handleSignOut}
          className="mt-3 w-full text-left text-xs text-ink-500 hover:text-ink-900"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen">
      <div className="flex">
        {/* Desktop sidebar (lg+) */}
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-ink-100 bg-paper px-5 py-6 sticky top-0 h-screen">
          <div className="mb-8">
            <div className="flex items-center gap-2">
              <img
                src="/pronerdz-logo.png"
                alt="ProNerdz"
                className="h-8 w-auto"
              />
              <span className="font-mono text-[10px] uppercase text-ink-400">
                {variant === "admin" ? "admin" : "portal"}
              </span>
            </div>
            <div className="mt-2 h-px bg-ink-200" />
          </div>

          {renderNav()}

          <div className="mt-auto pt-6">{renderSignedInCard()}</div>
        </aside>

        {/* Mobile/tablet top bar */}
        <header className="lg:hidden fixed top-0 inset-x-0 z-20 flex items-center justify-between border-b border-ink-100 bg-paper/95 px-4 py-3 backdrop-blur">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="-ml-1 rounded-lg p-2 text-ink-700 hover:bg-ink-100 active:bg-ink-200"
          >
            <HamburgerIcon />
            {/* Show the badge on the button when the drawer is closed so
                unread support stays visible without needing to open the menu. */}
            {mobileTotalUnread > 0 && (
              <span className="absolute mt-[-2px] ml-[-6px] rounded-full bg-accent-600 px-1.5 py-0.5 text-[9px] font-semibold text-white">
                {mobileTotalUnread}
              </span>
            )}
          </button>

          <span className="flex items-center gap-2">
            <img
              src="/pronerdz-logo.png"
              alt="ProNerdz"
              className="h-7 w-auto"
            />
            <span className="font-mono text-[10px] uppercase text-ink-400">
              {variant === "admin" ? "admin" : "portal"}
            </span>
          </span>

          {/* Spacer so the centered logo balances against the menu button on the left.
              Keep the same width as the menu button (40px) to keep the title centered. */}
          <span aria-hidden="true" className="w-10" />
        </header>

        {/* Mobile/tablet drawer */}
        {drawerOpen && (
          <div className="lg:hidden fixed inset-0 z-30 flex">
            {/* Backdrop */}
            <button
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
              className="flex-1 bg-ink-900/40"
            />
            {/* Panel */}
            <aside
              className="ml-auto flex w-72 max-w-[85vw] flex-col border-l border-ink-100 bg-paper px-5 py-6 shadow-xl"
              role="dialog"
              aria-modal="true"
            >
              <div className="mb-6 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <img
                    src="/pronerdz-logo.png"
                    alt="ProNerdz"
                    className="h-7 w-auto"
                  />
                  <span className="font-mono text-[10px] uppercase text-ink-400">
                    {variant === "admin" ? "admin" : "portal"}
                  </span>
                </span>
                <button
                  onClick={() => setDrawerOpen(false)}
                  aria-label="Close menu"
                  className="-mr-1 rounded-lg p-2 text-ink-500 hover:bg-ink-100"
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="mb-2 h-px bg-ink-200" />
              {renderNav("mt-2")}
              <div className="mt-auto pt-6">{renderSignedInCard()}</div>
            </aside>
          </div>
        )}

        <main className="flex-1 min-w-0 px-4 lg:px-10 py-8 pt-20 lg:pt-10">
          <div className="mx-auto max-w-6xl">{children}</div>
        </main>
      </div>
    </div>
  );
}

function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
